<!--
  Open in Nanome: sign-in (OAuth PKCE via @nanome-lite/bridge) -> pre-flight summary -> progress per pipeline step
  -> success with the workspace link, or an error state. The REST pipeline is `openInNanome` from @nanome-lite/bridge:
  it persists its progress, so Retry after a failure resumes at the failed step instead of creating a second
  workspace; AuthExpired (HTTP 401) signs the user out and keeps that progress; PreflightError explains what the
  free license or the file formats block; any later failure carries the partial workspace so it can be deleted or
  opened as it is.
-->
<script setup lang="ts">
import NanomeIcon from '@/components/NanomeIcon.vue';
import { computed, ref, toRaw, watch } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import Message from 'primevue/message';
import { useLiteStore } from '@nanome-lite/state';
import type { NanomeModel } from '@nanome-lite/data';
import { ApiError, AuthExpired, BridgeError, FREE_MAX_OWNED_WORKSPACES, FREE_MAX_WORKSPACE_ENTRIES, PreflightError, cleanupPartial, openInNanome, type OpenInNanomeResult, type PipelineStep, type PreflightDetails } from '@nanome-lite/bridge';
import { MARA_URL, useOAuth } from './oauth';

const visible = defineModel<boolean>('visible', { default: false });

const store = useLiteStore();
const oauth = useOAuth();

type Phase = 'preflight' | 'running' | 'success' | 'error';
interface StepRow { step: PipelineStep; message: string; index?: number; total?: number; done: boolean }
interface Failure {
  message: string;
  kind: 'auth' | 'preflight' | 'aborted' | 'other';
  preflight?: PreflightDetails;
  /** the workspace the run left behind (BridgeError.partial) */
  partial?: { workspaceId: string; url: string };
}

const phase = ref<Phase>('preflight');
const makePublic = ref(false);
const steps = ref<StepRow[]>([]);
const result = ref<OpenInNanomeResult | null>(null);
const failure = ref<Failure | null>(null);
const cleaning = ref(false);
let controller: AbortController | null = null;

const formats = computed(() => [...new Set(store.entries.map(e => e.source.ext.toUpperCase() + (e.source.gz ? '.GZ' : '')))]);
const defaultCount = computed(() => store.components.filter(c => c.isDefault).length);
const customCount = computed(() => store.components.length - defaultCount.value);
const tooMany = computed(() => store.entries.length > FREE_MAX_WORKSPACE_ENTRIES);
/** Server has no mol/mol2/bcif loader (PLAN.md pre-flight); the bridge converts these client-side. */
const converted = computed(() => store.entries.filter(e => ['mol', 'mol2', 'bcif', 'sd'].includes(e.source.ext)).map(e => e.name));
const maraHost = MARA_URL.replace(/^https?:\/\//, '');

watch(visible, (v) => { if (v && phase.value !== 'running') phase.value = 'preflight'; });

function describePreflight(d: PreflightDetails): string {
  switch (d.reason) {
    case 'license-workspaces': return `Your ${d.licenseType ?? 'free'} license already owns ${d.ownedWorkspaces ?? FREE_MAX_OWNED_WORKSPACES} of ${d.maxOwnedWorkspaces ?? FREE_MAX_OWNED_WORKSPACES} workspaces. Delete one on ${maraHost}, or delete the oldest workspace Lite created and retry.`;
    case 'license-entries': return `Your ${d.licenseType ?? 'free'} license allows ${d.maxEntries ?? FREE_MAX_WORKSPACE_ENTRIES} entries per workspace; ${d.entryCount ?? store.entries.length} are loaded. Remove some entries and retry.`;
    case 'unsupported-format': return `The server cannot load ${(d.entries ?? []).map(e => `${e.name} (${e.fileName}${e.note ? `: ${e.note}` : ''})`).join(', ') || 'one of the files'} and it could not be converted here.`;
    case 'no-entries': return 'Load a structure first.';
    case 'missing-model': return `${(d.entries ?? []).map(e => e.name).join(', ') || 'An entry'} has no parsed model to convert from; reload it and retry.`;
  }
}

async function run() {
  if (!oauth.token.value) return;
  phase.value = 'running';
  steps.value = [];
  failure.value = null;
  controller = new AbortController();
  try {
    result.value = await openInNanome({
      token: oauth.token.value,
      maraUrl: MARA_URL,
      entries: store.entries.map(e => toRaw(e)),
      models: new Map<number, NanomeModel>(store.models),
      components: store.components.map(c => toRaw(c)),
      makePublic: makePublic.value,
      signal: controller.signal,
      onProgress(step, message, index, total) {
        for (const s of steps.value) s.done = true;
        const row: StepRow = { step, message, done: step === 'done' };
        if (index !== undefined) row.index = index;
        if (total !== undefined) row.total = total;
        steps.value.push(row);
      },
    });
    for (const s of steps.value) s.done = true;
    phase.value = 'success';
  }
  catch (e) {
    failure.value = toFailure(e);
    if (failure.value.kind === 'auth') oauth.signOut();
    phase.value = 'error';
  }
  finally {
    controller = null;
  }
}

function toFailure(e: unknown): Failure {
  const message = e instanceof Error ? e.message : String(e);
  if (e instanceof AuthExpired) return { message: 'Your Nanome session is no longer valid. Sign in again and retry; the run resumes where it stopped.', kind: 'auth', ...(e.partial ? { partial: e.partial } : {}) };
  if (e instanceof PreflightError) return { message: describePreflight(e.details), kind: 'preflight', preflight: e.details };
  if (e instanceof ApiError && e.isAborted) return { message: 'Cancelled.', kind: 'aborted', ...(e.partial ? { partial: e.partial } : {}) };
  if (e instanceof BridgeError) return { message, kind: 'other', ...(e.partial ? { partial: e.partial } : {}) };
  return { message, kind: 'other' };
}

function cancel() { controller?.abort(); }

/** Delete the workspace a failed run left behind (or the oldest Lite-created one when the license cap hit). */
async function deleteWorkspace(workspaceId: string) {
  if (!oauth.token.value) return;
  cleaning.value = true;
  try {
    await cleanupPartial(workspaceId, { token: oauth.token.value, maraUrl: MARA_URL });
    store.toast('success', 'Workspace deleted');
    failure.value = null;
    phase.value = 'preflight';
  }
  catch (e) {
    if (e instanceof AuthExpired) oauth.signOut();
    store.toast('error', 'Could not delete the workspace', e instanceof Error ? e.message : String(e));
  }
  finally { cleaning.value = false; }
}

const oldestLiteWorkspace = computed(() => failure.value?.preflight?.liteWorkspaceIds?.[0] ?? null);

function openUrl(url: string) { window.open(url, '_blank', 'noopener'); }
function close() { visible.value = false; if (phase.value !== 'running') phase.value = 'preflight'; }
</script>

<template>
  <Dialog v-model:visible="visible" modal :draggable="false" :closable="phase !== 'running'" header="Open in Nanome" class="w-md rd-10" :dismissable-mask="phase !== 'running'">
    <!-- sign-in -->
    <div v-if="!oauth.isSignedIn.value && phase !== 'error'" class="flex flex-col gap-4">
      <p class="m-0 op-75 text-sm">
        Sign in with your Nanome account to create a workspace on {{ maraHost }} with these entries and their current representations.
      </p>
      <Message v-if="oauth.error.value" severity="error" :closable="false">
        {{ oauth.error.value }}
      </Message>
      <div class="flex gap-2">
        <Button icon="i-tabler-login" label="Sign in with Nanome" :loading="oauth.signingIn.value" rounded @click="oauth.signIn()" />
        <Button v-if="oauth.error.value" label="Retry with a fresh client" severity="secondary" rounded text @click="oauth.forgetAndRetry()" />
      </div>
      <p class="m-0 op-50 text-xs">
        A sign-in window opens on {{ maraHost }}. If your browser blocks it, this page redirects instead and comes back here.
      </p>
    </div>

    <!-- pre-flight -->
    <div v-else-if="phase === 'preflight'" class="flex flex-col gap-4">
      <div class="flex flex-col gap-1 text-sm">
        <div><b>{{ store.entries.length }}</b> {{ store.entries.length === 1 ? 'entry' : 'entries' }} <span v-if="formats.length" class="op-50">({{ formats.join(', ') }})</span></div>
        <div><b>{{ store.components.length }}</b> components: {{ defaultCount }} default, {{ customCount }} customised</div>
        <ul class="m-0 pl-5 op-75">
          <li v-for="e in store.entries" :key="e.serial">
            {{ e.name }} <span class="op-50">· {{ e.atomCount }} atoms<template v-if="e.modelCount > 1"> · model {{ e.currentModelSerial }}/{{ e.modelCount }}</template></span>
          </li>
        </ul>
      </div>
      <Message v-if="!store.entries.length" severity="warn" :closable="false">
        Load a structure first.
      </Message>
      <Message v-if="tooMany" severity="warn" :closable="false">
        Free licenses allow {{ FREE_MAX_WORKSPACE_ENTRIES }} entries per workspace; the server may reject this many.
      </Message>
      <Message v-if="converted.length" severity="info" :closable="false">
        {{ converted.join(', ') }} will be converted before upload (the server has no mol/mol2/bcif loader).
      </Message>
      <label class="flex items-center gap-2 text-sm cursor-pointer">
        <Checkbox v-model="makePublic" binary />
        Make this workspace public (anyone with the link can view)
      </label>
      <div class="flex items-center gap-2">
        <Button label="Create workspace" :disabled="!store.entries.length" rounded @click="run"><template #icon="iconProps"><NanomeIcon :class="iconProps.class" /></template></Button>
        <Button label="Sign out" severity="secondary" text rounded class="ml-auto" @click="oauth.signOut()" />
      </div>
    </div>

    <!-- progress / result -->
    <div v-else class="flex flex-col gap-4">
      <ol class="m-0 pl-0 list-none flex flex-col gap-1 text-sm max-h-60 of-y-auto">
        <li v-for="(s, i) in steps" :key="i" class="flex items-center gap-2">
          <i :class="s.done ? 'i-tabler-check text-primary' : phase === 'error' && i === steps.length - 1 ? 'i-tabler-alert-triangle text-red' : 'i-tabler-loader-2 animate-spin'" class="shrink-0" />
          <span class="truncate" :title="s.message">{{ s.message }}</span>
          <span v-if="s.total" class="ml-auto op-50 text-xs shrink-0">{{ s.index }}/{{ s.total }}</span>
        </li>
      </ol>

      <div v-if="phase === 'running'" class="flex gap-2">
        <Button label="Cancel" severity="secondary" text rounded class="ml-auto" @click="cancel" />
      </div>

      <template v-if="phase === 'success' && result">
        <Message severity="success" :closable="false">
          Workspace created<span v-if="result.resumed"> (resumed)</span><span v-if="result.code"> · join code <b>{{ result.code }}</b></span>.
        </Message>
        <Message v-for="w in result.warnings" :key="w" severity="warn" :closable="false">
          {{ w }}
        </Message>
        <div class="flex gap-2 items-center">
          <Button icon="i-tabler-external-link" label="Open workspace" rounded @click="openUrl(result.url)" />
          <a :href="result.url" target="_blank" rel="noopener" class="text-xs truncate min-w-0">{{ result.url }}</a>
          <Button label="Close" severity="secondary" text rounded class="ml-auto" @click="close" />
        </div>
      </template>

      <template v-else-if="phase === 'error' && failure">
        <Message :severity="failure.kind === 'aborted' ? 'warn' : 'error'" :closable="false">
          {{ failure.message }}
        </Message>
        <Message v-if="failure.partial" severity="info" :closable="false">
          A workspace was created before the failure. Retry resumes it; or delete it, or open it as it is.
        </Message>
        <div class="flex flex-wrap gap-2">
          <Button v-if="failure.kind === 'auth' || !oauth.isSignedIn.value" icon="i-tabler-login" label="Sign in again" rounded :loading="oauth.signingIn.value" @click="oauth.signIn()" />
          <Button v-else-if="failure.kind !== 'preflight'" icon="i-tabler-refresh" label="Retry" rounded @click="run" />
          <Button v-if="failure.partial" icon="i-tabler-external-link" label="Open anyway" severity="secondary" rounded @click="openUrl(failure.partial.url)" />
          <Button v-if="failure.partial && oauth.isSignedIn.value" icon="i-tabler-trash" label="Delete partial workspace" severity="danger" rounded text :loading="cleaning" @click="deleteWorkspace(failure.partial.workspaceId)" />
          <Button v-if="oldestLiteWorkspace && oauth.isSignedIn.value" icon="i-tabler-trash" label="Delete oldest Lite workspace" severity="danger" rounded text :loading="cleaning" @click="deleteWorkspace(oldestLiteWorkspace)" />
          <Button label="Back" severity="secondary" text rounded class="ml-auto" @click="phase = 'preflight'" />
        </div>
      </template>
    </div>
  </Dialog>
</template>
